/**
 * Utility functions for formatting data in the CoraFlow application
 */

/**
 * Format a patient ID to the standard format (PAT-XXXXX)
 * @param id - The patient ID number
 * @returns Formatted patient ID
 */
export function formatPatientId(id: string | number): string {
  const numericId = typeof id === 'string' ? parseInt(id, 10) : id;
  return `PAT-${numericId.toString().padStart(5, '0')}`;
}

/**
 * Normalize any date-of-birth value the backend might emit (YYYY-MM-DD string,
 * ISO timestamp, or a JS Date) to a plain YYYY-MM-DD civil-date string. The
 * backend now stores DOB as a string, but older mock data + some legacy paths
 * may still supply timestamps; this keeps the UI layer timezone-free.
 */
export function formatCivilDate(value: Date | string | null | undefined): string {
  if (!value) return '';
  if (typeof value === 'string') {
    // Already YYYY-MM-DD or an ISO string — take the date portion verbatim.
    return value.slice(0, 10);
  }
  // Fall back to local-time components so we don't shift the day via UTC.
  const y = value.getFullYear();
  const m = String(value.getMonth() + 1).padStart(2, '0');
  const d = String(value.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Calculate age from a date of birth. For civil-date strings (YYYY-MM-DD) we
 * parse the components directly — never round-trip through `new Date(string)`,
 * which interprets the value as UTC midnight and can drop a day in non-UTC
 * locales.
 */
export function calculateAge(dateOfBirth: Date | string): number {
  const iso = formatCivilDate(dateOfBirth);
  if (!iso) return 0;
  const [yearStr, monthStr, dayStr] = iso.split('-');
  const birthYear = Number(yearStr);
  const birthMonth = Number(monthStr);
  const birthDay = Number(dayStr);

  const today = new Date();
  const todayYear = today.getFullYear();
  const todayMonth = today.getMonth() + 1;
  const todayDay = today.getDate();

  let age = todayYear - birthYear;
  if (todayMonth < birthMonth || (todayMonth === birthMonth && todayDay < birthDay)) {
    age--;
  }
  return age;
}

// A civil (calendar) date, the shape the backend returns DATE columns in.
const CIVIL_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

const isRealCivilDate = (iso: string): boolean => {
  const match = CIVIL_DATE_PATTERN.exec(iso);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  // Date.UTC rolls an impossible date over (2024-02-30 becomes 2024-03-01), so a round trip exposes it.
  const probe = new Date(Date.UTC(year, month - 1, day));
  return probe.getUTCFullYear() === year && probe.getUTCMonth() === month - 1 && probe.getUTCDate() === day;
};

/**
 * `calculateAge`, but null when the date of birth is missing, not a real date,
 * or in the future. An unknown age must stay unknown: 0 would read as a newborn.
 */
export function calculateAgeOrNull(dateOfBirth: Date | string | null | undefined): number | null {
  const iso = formatCivilDate(dateOfBirth);
  if (!isRealCivilDate(iso)) return null;
  const age = calculateAge(iso);
  return age >= 0 ? age : null;
}

/**
 * Short localized date, e.g. "Mar 15, 2027". `new Date('YYYY-MM-DD')` reads a
 * civil date as UTC midnight, so civil dates are formatted in UTC to keep their
 * calendar day west of Greenwich. Timestamps are shown in `timeZone`, which
 * defaults to the viewer's own zone.
 */
export function formatShortDate(value: string, bcp47Locale: string, timeZone?: string): string {
  return new Date(value).toLocaleDateString(bcp47Locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: CIVIL_DATE_PATTERN.test(value) ? 'UTC' : timeZone,
  });
}

const MISSING_DATE = '—';

const isReadableDate = (value: string | null | undefined): value is string => !!value && !Number.isNaN(new Date(value).getTime());

/**
 * Numeric date, e.g. "24/10/2026" in es-ES. Pass the app language's tag (see
 * useDateLocale), never the browser's. Civil dates and `timeZone` follow
 * formatShortDate; '—' when the value is missing or cannot be read.
 */
export function formatNumericDate(value: string | null | undefined, bcp47Locale: string, timeZone?: string): string {
  if (!isReadableDate(value)) return MISSING_DATE;
  return new Date(value).toLocaleDateString(bcp47Locale, { timeZone: CIVIL_DATE_PATTERN.test(value) ? 'UTC' : timeZone });
}

/**
 * A calendar date such as a date of birth, as formatNumericDate writes it. A timestamp is cut to its stored date
 * first, so the day never moves with the viewer's time zone.
 */
export function formatCalendarDate(value: Date | string | null | undefined, bcp47Locale: string): string {
  return formatNumericDate(formatCivilDate(value), bcp47Locale);
}

/**
 * A date with the given Intl options, following formatShortDate's rule: a calendar date (`YYYY-MM-DD`) keeps its own
 * day wherever the viewer is, a timestamp is shown in the viewer's zone.
 */
export function formatDateWith(value: string, bcp47Locale: string, options: Intl.DateTimeFormatOptions): string {
  return new Date(value).toLocaleDateString(bcp47Locale, { ...options, timeZone: CIVIL_DATE_PATTERN.test(value) ? 'UTC' : options.timeZone });
}

function formatCalendarDateInWords(value: Date | string | null | undefined, bcp47Locale: string, options: Intl.DateTimeFormatOptions): string {
  const civilDate = formatCivilDate(value);
  if (!CIVIL_DATE_PATTERN.test(civilDate)) return MISSING_DATE;
  return formatDateWith(civilDate, bcp47Locale, options);
}

/** A calendar date in words with its weekday, e.g. "lunes, 12 de octubre de 2026" in es-ES; '—' when it cannot be read. */
export function formatLongCalendarDate(value: Date | string | null | undefined, bcp47Locale: string): string {
  return formatCalendarDateInWords(value, bcp47Locale, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
}

/** A calendar date as a day heading, without the year, e.g. "lunes, 12 de octubre" in es-ES; '—' when it cannot be read. */
export function formatDayHeading(value: Date | string | null | undefined, bcp47Locale: string): string {
  return formatCalendarDateInWords(value, bcp47Locale, { weekday: 'long', month: 'long', day: 'numeric' });
}

const MAX_SHOWN_FRACTION_DIGITS = 3;
const PERCENT_FRACTION_DIGITS = 1;

/**
 * A number written the `bcp47Locale` way, e.g. "39,2" in es-ES. With `fixedFractionDigits` it always shows that many
 * decimals, as toFixed does; without it, the decimals the value has.
 */
export function formatDecimal(value: number, bcp47Locale: string, fixedFractionDigits?: number): string {
  const digits =
    fixedFractionDigits === undefined
      ? { maximumFractionDigits: MAX_SHOWN_FRACTION_DIGITS }
      : { minimumFractionDigits: fixedFractionDigits, maximumFractionDigits: fixedFractionDigits };
  return new Intl.NumberFormat(bcp47Locale, digits).format(value);
}

/**
 * A share out of 100 written the `bcp47Locale` way, e.g. "12,5 %" in es-ES. With `fixedFractionDigits` it always shows
 * that many decimals; without it, at most one.
 */
export function formatPercent(percent: number, bcp47Locale: string, fixedFractionDigits?: number): string {
  const digits =
    fixedFractionDigits === undefined
      ? { maximumFractionDigits: PERCENT_FRACTION_DIGITS }
      : { minimumFractionDigits: fixedFractionDigits, maximumFractionDigits: fixedFractionDigits };
  return new Intl.NumberFormat(bcp47Locale, { style: 'percent', ...digits }).format(percent / 100);
}

/** Numeric date and time to the second, e.g. "10/10/2026, 19:02:02" in es-ES; same rules as formatNumericDate. */
export function formatNumericDateTime(value: string | null | undefined, bcp47Locale: string, timeZone?: string): string {
  if (!isReadableDate(value)) return MISSING_DATE;
  return new Date(value).toLocaleString(bcp47Locale, { timeZone });
}

/** Short-month date and time to the minute, e.g. "10 oct 2026, 19:02" in es-ES; same rules as formatNumericDate. */
export function formatMediumDateTime(value: string | null | undefined, bcp47Locale: string, timeZone?: string): string {
  if (!isReadableDate(value)) return MISSING_DATE;
  return new Date(value).toLocaleString(bcp47Locale, { dateStyle: 'medium', timeStyle: 'short', timeZone });
}

/**
 * Format queue number to standard format (LETTER-NUMBER)
 * @param letter - Queue letter (A-Z)
 * @param number - Queue number
 * @returns Formatted queue number (e.g., "A-023")
 */
export function formatQueueNumber(letter: string, number: number): string {
  return `${letter.toUpperCase()}-${number.toString().padStart(3, '0')}`;
}

/**
 * Validate email format
 * @param email - Email address to validate
 * @returns True if email is valid
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Map a UI locale (vue-i18n's short code: `en`/`pt`/`es`) to the BCP-47 tag
 * that `Intl` APIs (NumberFormat, DateTimeFormat) should use — so e.g. `pt`
 * resolves to European Portuguese (pt-PT), not the runtime default (often
 * pt-BR). Defaults to en-US for unmapped locales. Single source of truth for
 * this mapping across the frontend (number/date formatting, PDF export); Vue
 * components reach it through the `useDateLocale` composable.
 */
const UI_LOCALE_TO_BCP47: Record<string, string> = {
  en: 'en-US',
  pt: 'pt-PT',
  es: 'es-ES',
};

export function localeToBcp47(uiLocale: string): string {
  return UI_LOCALE_TO_BCP47[uiLocale] ?? 'en-US';
}

// AOA and MZN are usually priced in whole units, so their decimals are left out when an amount has no cents
// ("1 000 Kz"); an amount with cents always shows them ("34,20 Kz"), since a bill never hides cents. Same rule as
// the backend receipts (apps/backend/src/shared/money-format.ts). EUR/USD always keep two decimals.
const WHOLE_UNIT_CURRENCIES: ReadonlySet<string> = new Set(['AOA', 'MZN']);
const CENTS_FRACTION_DIGITS = 2;
const WHOLE_UNITS_FRACTION_DIGITS = 0;
const CENTS_PER_UNIT = 100;

const hasCents = (amount: number): boolean => Math.round(amount * CENTS_PER_UNIT) % CENTS_PER_UNIT !== 0;

let knownCurrencies: ReadonlySet<string> | null | undefined;

// Intl writes any well-formed code it does not know as the code itself ("30,00 XYZ"); those fall back to the
// hospital's symbol, as the backend does. Intl.supportedValuesOf is newer than this app's ES2020 lib typings.
const isKnownCurrency = (currencyCode: string): boolean => {
  if (knownCurrencies === undefined) {
    const supportedValuesOf = (Intl as { supportedValuesOf?: (key: 'currency') => string[] }).supportedValuesOf;
    knownCurrencies = typeof supportedValuesOf === 'function' ? new Set(supportedValuesOf('currency')) : null;
  }
  return knownCurrencies?.has(currencyCode) ?? true;
};

// Currencies where Intl's "currency" style renders the ISO code (e.g.
// "AOA 1,000") instead of the locally-recognized symbol. For those we skip
// Intl currency formatting and append the explicit `currencySymbol` from the
// hospital record so staff see "1 000 Kz" as expected.
const FORCE_SYMBOL_FALLBACK: ReadonlySet<string> = new Set(['AOA', 'MZN']);

/**
 * Format a money amount in the hospital's currency. Falls back to a neutral
 * decimal + symbol when the ISO code isn't recognized by the runtime's
 * Intl implementation (e.g., regional codes like AOA on older browsers).
 *
 * Pass the active hospital's `currencyCode` (ISO 4217) and the matching
 * `currencySymbol` from `HospitalDto` so the display still reads sensibly
 * when the formatter can't resolve the code on its own.
 */
export function formatMoney(
  amount: number,
  currencyCode: string | null | undefined,
  currencySymbol: string | null | undefined,
  uiLocale: string = 'en',
): string {
  const bcp47 = localeToBcp47(uiLocale);
  const showsWholeUnits = Boolean(currencyCode && WHOLE_UNIT_CURRENCIES.has(currencyCode)) && !hasCents(amount);
  const fractionDigits = showsWholeUnits ? WHOLE_UNITS_FRACTION_DIGITS : CENTS_FRACTION_DIGITS;

  if (currencyCode && !FORCE_SYMBOL_FALLBACK.has(currencyCode) && isKnownCurrency(currencyCode)) {
    try {
      return new Intl.NumberFormat(bcp47, {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      }).format(amount);
    } catch {
      // Fall through to manual symbol formatting below.
    }
  }
  const number = new Intl.NumberFormat(bcp47, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(amount);
  return currencySymbol ? `${number} ${currencySymbol}` : number;
}
